import { useParams, Link } from 'react-router-dom';
import { categories as allCategories } from '@/mocks/home';
import { getArticlesByCategory, getCategoryName } from '@/mocks/articles';
import SEO from '@/components/feature/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const categoryName = getCategoryName(slug || '');
  const articles = getArticlesByCategory(slug || '');

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${categoryName} - 爱风月`,
        description: `爱风月${categoryName}分类，汇聚${categoryName}相关文章与美学内容`,
        url: `https://aifengyue.com/category/${slug}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://aifengyue.com/' },
          { '@type': 'ListItem', position: 2, name: '风月雅集', item: 'https://aifengyue.com/category/fengya-shenghuo' },
          { '@type': 'ListItem', position: 3, name: categoryName },
        ],
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${categoryName} - 爱风月 | 风雅生活美学`}
        description={`爱风月${categoryName}分类页面，汇聚${categoryName}相关文章、生活美学与风月文化内容。探索${categoryName}的雅致与情韵。`}
        keywords={`爱风月,${categoryName},风月文化,风雅生活美学,诗意浪漫`}
        canonicalUrl={`https://aifengyue.com/category/${slug}`}
        schema={categorySchema}
      />

      <Navbar />

      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '风月雅集', href: '/category/fengya-shenghuo' },
              { label: categoryName },
            ]}
          />

          <div className="mt-8 mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
              {categoryName}
            </h1>
            <p className="text-foreground-500 text-sm leading-relaxed max-w-2xl">
              {allCategories.find((c) => c.slug === slug)?.description || ''}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            {allCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/50 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl" />
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}